// What the app ships for a recipe, and where its steps go instead.
//
// Steps are about 40% of the catalog's weight but only the recipe page and cook mode read them, so
// they load on demand (a JSON file; `virtual:recipe-steps` in vite.config.ts) rather than with the
// first screen. Each recipe keeps `stepCount` for places that only show how many there are.
//
// Used by the build (vite.config.ts) and by dev and tests (recipes.ts), so every recipe the app
// holds has the same shape. Imports nothing from the app, so the build config can load it.

/** A recipe without its steps, which are counted instead. */
export function toShipped<T extends { steps: unknown[] }>(r: T): Omit<T, 'steps'> & { stepCount: number } {
  const { steps, ...rest } = r;
  return { ...rest, stepCount: steps.length };
}

/** Build only: drops metadata the app never renders (the image brief, review details). */
export function trimForApp<T extends { meta: { visual: { palette: unknown } }; review: { status: string } }>(r: T): T {
  return { ...r, meta: { ...r.meta, visual: { palette: r.meta.visual.palette } }, review: { status: r.review.status } } as T;
}

/** The on-demand module's contents: each recipe's steps by id. */
export function stepsById<T extends { id: string; steps: unknown[] }>(recipes: readonly T[]): Record<string, T['steps']> {
  return Object.fromEntries(recipes.map((r) => [r.id, r.steps]));
}
